<template>
  <Modal
    :has_parent="false"
    :is_small="false"
    :prevent_enter_shortcut="true"
    :is_confirmed="!is_button_primary_disabled"
    data-test="project-modal"
    @actionCancel="$parent.$parent.close"
    @actionPrimary="onSubmit"
    @actionSecondary="onBack"
  >
    <template #title>
      {{ $t(`project.modals.add_project.step_${current_step}.title`) }}
    </template>
    <template
      v-if="$te(`project.modals.add_project.step_${current_step}.subtitle`)"
      #subtitle
    >
      {{ $t(`project.modals.add_project.step_${current_step}.subtitle`) }}
    </template>
    <template
      v-if="current_step === 1"
      #content
    >
      <b-field :label="`${$t('project.labels.projectName')}`">
        <div class="field w-auto md:w-4/6 lg:w-3/6">
          <input
            v-model.trim="v$.project.attributes.name.$model"
            :class="{ 'is-danger': v$.project.attributes.name.$error }"
            class="input"
            data-test="questionlist-name-input"
            type="text"
            @input="getAutocompleteProjects($event.target.value)"
          />
          <div v-if="v$.project.attributes.name.$error">
            <p
              v-if="v$.project.attributes.name.required.$invalid"
              class="help is-danger"
            >
              {{ $t('project.validation.needName') }}
            </p>
            <p
              v-if="v$.project.attributes.name.uniqueName.$invalid"
              class="help is-danger"
            >
              {{ $t('validation.nameAlreadyTaken') }}
            </p>
          </div>
        </div>
      </b-field>
      <b-switch
        v-model="project.attributes.skip_customer"
        class="mb-6"
        @change="toggleTUP"
      >
        {{ $t('project.labels.isTemplateProject') }}
      </b-switch>
    </template>
    <template
      v-if="current_step === 2"
      #content
    >
      <div class="columns">
        <div class="column is-offset-1 is-5 text-center">
          <Card
            :class="project.attributes.skip_unit ? 'bg-primary-500! text-lg font-bold text-white!' : ''"
            class="cursor-pointer justify-center"
            @click="initSUP"
          >
            <template #content>
              {{ $t('project.labels.singleUnit') }}
            </template>
          </Card>
        </div>
        <div class="column is-5 text-center">
          <Card
            :class="!project.attributes.skip_unit ? 'bg-primary-500! text-lg font-bold text-white!' : ''"
            class="cursor-pointer justify-center"
            @click="initMUP"
          >
            <template #content>
              {{ $t('project.labels.multiUnits') }}
            </template>
          </Card>
        </div>
      </div>
    </template>
    <template
      v-if="current_step === 3"
      #content
    >
      <section class="w-full">
        <div class="grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          <div
            v-for="level_option in level_options"
            :key="level_option.key"
            class="flex p-2"
          >
            <div
              :class="{
                'cursor-not-allowed': level_option.disabled,
                'cursor-pointer': !level_option.disabled,
              }"
              class="relative flex w-full flex-col rounded-2xl border border-slate-300 bg-white p-4 shadow-sm transition hover:shadow-md"
              @click="toggleAttribute(level_option.attribute)"
            >
              <div class="mb-3 flex items-center">
                <FontAwesomeIcon
                  :icon="level_option.icon"
                  class="mr-2"
                />
                <span class="hidden font-semibold md:inline">
                  {{ $truncate(level_option.label, 12) }}
                </span>
                <span class="font-semibold md:hidden">
                  {{ $truncate(level_option.label, 32) }}
                </span>
              </div>

              <p class="mb-12 grow">
                {{ level_option.explainer }}
              </p>

              <div
                v-if="!project.attributes[level_option.attribute] && !level_option.disabled"
                class="absolute right-3 bottom-3 left-3 flex items-center justify-center rounded-full bg-green-500 px-3 py-1 text-xs font-medium text-white shadow-sm"
              >
                <FontAwesomeIcon
                  :icon="['fas', 'check']"
                  class="mr-2"
                />
                {{ $t('project.labels.selected') }}
              </div>

              <div
                v-else-if="project.attributes[level_option.attribute] && !level_option.disabled"
                class="absolute right-3 bottom-3 left-3 flex items-center justify-center rounded-full bg-slate-600 px-3 py-1 text-xs font-medium text-white shadow-sm"
              >
                {{ $t('project.labels.unselected') }}
              </div>

              <div
                v-else-if="level_option.disabled"
                class="absolute right-3 bottom-3 left-3 flex items-center justify-center rounded-full bg-green-500 px-3 py-1 text-xs font-medium text-white shadow-sm"
              >
                {{ $t('project.labels.included') }}
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <template
      v-if="current_step === 4"
      #content
    >
      <div
        v-if="$flipper.enabled($FT.TEMPLATE_PROJECTS) && !project.attributes.skip_customer"
        class="mb-6"
        style="height: 250px"
      >
        <SelectProject
          :active="project_template"
          :label="$t('project.modals.add_project.step_4.placeholder')"
          :projects_props="projects_template"
          @changeSelect="(pt) => (project_template = pt)"
        />
        <section
          v-if="project_template"
          class="mt-5 ml-5"
        >
          <b-field v-if="$flipper.enabled($FT.MILESTONES)">
            <b-checkbox v-model="features.milestones">
              {{ $t('navbar.employee.project.milestones') }}
            </b-checkbox>
          </b-field>
          <b-field v-if="$flipper.enabled($FT.TODOS)">
            <b-checkbox v-model="features.todo_lists">
              {{ $t('navbar.employee.project.todos') }}
            </b-checkbox>
          </b-field>
          <b-field v-if="$flipper.enabled($FT.DOCUMENTS)">
            <b-checkbox v-model="features.documents">
              {{ $t('navbar.employee.project.documents') }}
            </b-checkbox>
          </b-field>
          <b-field v-if="$flipper.enabled($FT.DECISIONS_CORE)">
            <b-checkbox
              v-if="project.attributes.skip_unit"
              v-model="features.decisions"
            >
              {{ $t('navbar.employee.project.decisions') }}
            </b-checkbox>
          </b-field>
          <b-field v-if="$flipper.enabled($FT.ANNOUNCEMENTS)">
            <b-checkbox v-model="features.announcements">
              {{ $t('navbar.employee.project.news') }}
            </b-checkbox>
          </b-field>
          <b-field v-if="project.attributes.skip_unit && $flipper.enabled($FT.SURVEYS)">
            <b-checkbox v-model="features.surveys">
              {{ $t('navbar.employee.project.surveys') }}
            </b-checkbox>
          </b-field>
          <b-field v-if="$flipper.enabled($FT.FAQS)">
            <b-checkbox v-model="features.question_lists">
              {{ $t('navbar.employee.project.question_lists') }}
            </b-checkbox>
          </b-field>
        </section>
        <br />
        <div
          v-if="project_template && !project.attributes.skip_unit"
          class="message is-primary"
        >
          <div class="message-body flex">
            <FontAwesomeIcon
              class="has-text-primary mr-3"
              icon="circle-info"
              size="lg"
            />
            <div>{{ $t('project.modals.add_project.step_4.info') }}</div>
          </div>
        </div>
      </div>
      <div
        v-else-if="!$flipper.enabled($FT.TEMPLATE_PROJECTS)"
        class="message is-primary p-5"
      >
        <div v-html="$t(`upsell.project_templates.description`)" />
        <div class="mt-4 italic">
          {{ $t('decision_type.labels.upsellCategoryTypes') }}
        </div>
      </div>
    </template>
    <template #ButtonPrimary>
      {{ button_primary_text }}
    </template>
    <template
      v-if="current_step > 1"
      #ButtonSecondary
    >
      {{ $t('modal.actions.goBack') }}
    </template>
  </Modal>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import Modal from '@/app/shared_components/modals/TheModal.vue';
import { mapActions, mapGetters } from 'vuex';
import { required } from '@vuelidate/validators';
import { ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import SelectProject from '@/app/shared_components/selects/SelectProject.vue';
import { PROJECT_TYPES, Tenant } from '@/app/data/model_constants';

export default {
  name: 'ProjectModal',

  components: { SelectProject, Modal },

  setup() {
    return { v$: useVuelidate() };
  },

  data() {
    return {
      current_step: 1,
      project: {
        attributes: {
          name: '',
          skip_phase: null,
          skip_lot: null,
          skip_building: null,
          skip_unit: null,
          skip_customer: null,
          status: 'enabled',
        },
      },
      project_template: null,
      features: {
        milestones: true,
        todo_lists: true,
        documents: true,
        decisions: true,
        surveys: true,
        question_lists: true,
        announcements: true,
      },
      projects_autocomplete: [],
      is_checking_name: false,
      Tenant,
    };
  },

  validations: {
    project: {
      attributes: {
        name: {
          required,
          uniqueName(val) {
            if (!val) {
              return true;
            }
            const name_exists_autocomplete = _.some(
              this.projects_autocomplete,
              (project) => project.attributes.name.toLowerCase() === val.toLowerCase(),
            );

            return !name_exists_autocomplete;
          },
        },
      },
    },
  },

  computed: {
    ...mapGetters(['current_client', 'projects']),

    is_button_primary_disabled() {
      if (this.current_step === 1) {
        return this.v$.project.attributes.name.$invalid || this.is_checking_name;
      }
      return false;
    },

    projects_template() {
      // TODO: this needs pagination with backend filtering
      return _.chain(this.projects)
        .filter({ attributes: { skip_customer: true } })
        .orderBy(['attributes.name'], ['asc'])
        .value();
    },

    button_primary_text() {
      if (this.project.attributes.skip_customer || this.current_step === 4) {
        return this.$t('project.actions.submit');
      }

      return this.$t('project.actions.next');
    },

    level_options() {
      return {
        projects: {
          label: this.project.attributes.name,
          value: true,
          disabled: true,
          explainer: this.$t('project.labels.levelProjectExplainer'),
          icon: 'layer-group',
        },
        phase: {
          attribute: 'skip_phase',
          label: this.$t('levels.phase'),
          value: !this.project.attributes.skip_phase,
          explainer: this.$t('project.labels.levelPhaseExplainer'),
          icon: 'calendar-days',
        },
        lot: {
          attribute: 'skip_lot',
          label: this.$t('levels.lot'),
          value: !this.project.attributes.skip_lot,
          explainer: this.$t('project.labels.levelPlotExplainer'),
          icon: 'chart-tree-map',
        },
        building: {
          attribute: 'skip_building',
          label: this.$t('levels.building'),
          value: !this.project.attributes.skip_building,
          explainer: this.$t('project.labels.levelBuildingExplainer'),
          icon: 'building',
        },
        units: {
          label: this.$t('levels.unit'),
          value: true,
          disabled: true,
          explainer: this.$t('project.labels.levelUnitExplainer'),
          icon: 'house',
        },
      };
    },

    project_type() {
      return this.current_client.attributes.project_type;
    },
  },

  created() {
    if (this.project_type === PROJECT_TYPES.MUP) {
      this.initMUP();
    } else {
      this.initSUP();
    }

    this.autocompleteProjects = _.debounce(async (search = '') => {
      if (!search) {
        this.projects_autocomplete = [];
        return;
      }

      try {
        this.is_checking_name = true;
        const results = await this.AUTOCOMPLETE_PROJECTS({ name_cont: search });
        this.projects_autocomplete = results || [];
        this.v$.project.attributes.name.$touch();
      } catch (error) {
        this.projects_autocomplete = [];
      } finally {
        this.is_checking_name = false;
      }
    }, 300);
  },

  methods: {
    ...mapActions(['CREATE_PROJECT', 'DUPLICATE_PROJECT', 'AUTOCOMPLETE_PROJECTS']),

    getAutocompleteProjects(search = '') {
      this.autocompleteProjects(search);
    },

    toggleAttribute(attribute) {
      this.project.attributes[attribute] = !this.project.attributes[attribute];
    },

    toggleTUP() {
      return this.project.attributes.skip_customer ? this.initTUP() : this.initSUP();
    },

    initSUP() {
      this.project.attributes.skip_phase = true;
      this.project.attributes.skip_lot = true;
      this.project.attributes.skip_building = true;
      this.project.attributes.skip_unit = true;
      this.project.attributes.skip_customer = false;
    },

    initTUP() {
      this.initSUP();
      this.project.attributes.skip_customer = true;
    },

    initMUP() {
      this.project.attributes.skip_phase = true;
      this.project.attributes.skip_lot = true;
      this.project.attributes.skip_building = true;
      this.project.attributes.skip_unit = false;
      this.project.attributes.skip_customer = false;
    },
    onSubmit() {
      this.v$.$touch();
      if (!this.v$.$invalid) {
        switch (this.current_step) {
          // define name and whether it's a template project
          case 1:
            // project template => single step, immediately create project
            if (this.project.attributes.skip_customer) return this.createProject();
            switch (this.project_type) {
              case PROJECT_TYPES.MUP:
                this.current_step = 3;
                return;
              case PROJECT_TYPES.SUP:
                if (this.$flipper.enabled(this.$FT.TEMPLATE_PROJECTS)) {
                  this.current_step = 4;
                  return;
                }
                return this.createProject();
              default:
                this.current_step = 2;
                return;
            }

          // decide on mup or sup project
          case 2:
            switch (this.project.attributes.skip_unit) {
              case true:
                this.current_step = 4;
                return;
              default:
                this.current_step = 3;
                return;
            }

          // mup project: define sub levels
          case 3:
            this.current_step = 4;
            return;

          // mup/sup project: copy from template (optional)
          case 4:
            return this.createProject();
          default:
        }
        throw new Error('Invalid step');
      }
    },

    onBack() {
      if (this.current_step === 4) {
        let decrement;
        if (!this.project.attributes.skip_unit) {
          // MUP project
          decrement = 1;
        } else {
          decrement = this.project_type === PROJECT_TYPES.MUP_AND_SUP ? 2 : 3;
        }
        this.current_step -= decrement;
        return;
      }
      if (this.project_type === PROJECT_TYPES.MUP && this.current_step === 3) {
        this.current_step -= 2;
        return;
      }
      this.current_step -= 1;
    },

    async createProject() {
      try {
        const project = await this.CREATE_PROJECT({ record: this.project });
        if (this.project_template) {
          await this.DUPLICATE_PROJECT({
            record: {
              id_template: this.project_template.id,
              id_duplicate: project.id,
              attributes: {
                features: this.features,
              },
            },
          });
        }
        const route_name = this.project.attributes.skip_customer
          ? ROUTES_EMPLOYEES.PROJECT_MILESTONES
          : ROUTES_EMPLOYEES.PROJECT_INFORMATION_LEVELS;
        if (!this.project.attributes.skip_customer) {
          this.$confetti.start();
          setTimeout(() => this.$confetti.stop(), 5000);
        }
        this.$router.push({
          name: route_name,
          params: { project_id: project.id, level_type: project.type, level_id: project.id },
        });
      } catch (e) {
        if (e.response.status === 422) {
          const errors = e.response.data.errors;
          const errorKey = Object.keys(errors)[0];
          const errorMessage = errors[errorKey][0];
          this.toast_fail(this.$t(`project.errors.${errorKey}.${errorMessage}`));
        }
        this.toast_action_failed();
      } finally {
        this.$parent.$parent.close();
      }
    },
  },
};
</script>
